/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 *
 * Barra operativa única: tabs (POS…) y ShellStack (Integraciones, detalle…).
 * Título de módulo + trigger Square de sucursal (kicker · nombre · ▾) en mobile /
 * web angosto. En web con sidebar no se muestra (Sucursal vive en el rail).
 */
import React from 'react';
import { Platform, Pressable, StyleSheet, Text, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { ticketColors, ticketFonts } from '../../shared/theme/ticketColors';
import {
  WEB_HEADER_TITLE_RIGHT_NARROW,
  WEB_HEADER_TITLE_RIGHT_WITH_REFRESH,
  WebTabDataRefreshButton,
} from './WebTabDataRefreshButton';

/** Ancho reservado a la derecha del título (debe coincidir con el rail de acciones). */
export const APP_HEADER_ACTIONS_RAIL_WIDTH =
  Platform.OS === 'web'
    ? WEB_HEADER_TITLE_RIGHT_WITH_REFRESH
    : WEB_HEADER_TITLE_RIGHT_NARROW;

export const APP_HEADER_BACK_SLOT_WIDTH = 44;

/** Altura del contenido de toolbar sin status bar. */
export function getOperationalHeaderBarHeight(hasModuleTitle: boolean): number {
  if (hasModuleTitle) {
    return Platform.OS === 'ios' ? 56 : 60;
  }
  return Platform.OS === 'ios' ? 44 : 48;
}

export const APP_TAB_HEADER_BAR_HEIGHT = getOperationalHeaderBarHeight(true);

type OperationalHeaderTitleProps = {
  title?: string;
  branchName?: string;
  onPressBranch?: () => void;
};

function BranchChip({
  branchName,
  onPressBranch,
}: {
  branchName?: string;
  onPressBranch?: () => void;
}) {
  const label = branchName ?? (onPressBranch ? 'Elegí sucursal' : null);
  if (!label) return null;

  const a11yLabel = branchName ? 'Cambiar de sucursal' : 'Elegir sucursal';
  const content = (
    <>
      <Text style={headerStyles.branchKicker}>Sucursal</Text>
      <View style={headerStyles.branchNameRow}>
        <Text style={headerStyles.branchName} numberOfLines={1}>
          {label}
        </Text>
        {onPressBranch ? (
          <Ionicons name="chevron-down" size={12} color={ticketColors.ink2} />
        ) : null}
      </View>
    </>
  );

  if (onPressBranch) {
    return (
      <Pressable
        onPress={onPressBranch}
        accessibilityRole="button"
        accessibilityLabel={a11yLabel}
        hitSlop={8}
        style={headerStyles.branchTrigger}
        testID="auth.branch-chip"
      >
        {content}
      </Pressable>
    );
  }

  return <View style={headerStyles.branchTrigger}>{content}</View>;
}

/** Título de módulo (MainTabs) + chip de sucursal. */
export function OperationalHeaderTitle({
  title,
  branchName,
  onPressBranch,
}: OperationalHeaderTitleProps) {
  const trimmedTitle = title?.trim() ?? '';
  const hasTitle = trimmedTitle.length > 0;

  if (!hasTitle && !branchName && !onPressBranch) {
    return <View style={headerStyles.titleBlock} />;
  }

  return (
    <View style={[headerStyles.titleBlock, hasTitle && headerStyles.titleBlockStacked]}>
      {hasTitle ? (
        <Text style={headerStyles.moduleTitle} numberOfLines={1}>
          {trimmedTitle}
        </Text>
      ) : null}
      <BranchChip branchName={branchName} onPressBranch={onPressBranch} />
    </View>
  );
}

/** Rail fijo a la derecha: web = actualizar pantalla; nativo = (vacío tras eliminar sync UI). */
export function AppHeaderActions() {
  const isWeb = Platform.OS === 'web';

  if (!isWeb) {
    return <View style={[headerStyles.actionsRail, { width: APP_HEADER_ACTIONS_RAIL_WIDTH }]} />;
  }

  return (
    <View
      style={[
        headerStyles.actionsRail,
        { width: APP_HEADER_ACTIONS_RAIL_WIDTH },
      ]}
    >
      <WebTabDataRefreshButton />
    </View>
  );
}

export type AppOperationalHeaderBarProps = {
  /** Nombre de módulo / pantalla (MainTabs y ShellStack). */
  title?: string;
  branchName?: string;
  onPressBranch?: () => void;
  /** Flecha atrás u otro control izquierdo (ShellStack mobile). */
  leading?: React.ReactNode;
};

/** Barra completa — única implementación para tabs y shell. */
export function AppOperationalHeaderBar({
  title,
  branchName,
  onPressBranch,
  leading,
}: AppOperationalHeaderBarProps) {
  const hasModuleTitle = Boolean(title?.trim());
  const barHeight = getOperationalHeaderBarHeight(hasModuleTitle);

  return (
    <View style={headerStyles.root}>
      <View
        style={[
          headerStyles.bar,
          { height: barHeight },
          leading ? headerStyles.barWithLeading : null,
        ]}
      >
        {leading ? <View style={headerStyles.leadingSlot}>{leading}</View> : null}
        <View style={headerStyles.titleLane}>
          <OperationalHeaderTitle
            title={title}
            branchName={branchName}
            onPressBranch={onPressBranch}
          />
        </View>
        <AppHeaderActions />
      </View>
    </View>
  );
}

const headerStyles = StyleSheet.create({
  root: {
    backgroundColor: ticketColors.paper,
    borderBottomWidth: 1,
    borderBottomColor: ticketColors.ruleSoft,
  },
  bar: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingLeft: 16,
    paddingRight: 0,
    gap: 8,
    minWidth: 0,
  },
  barWithLeading: {
    paddingLeft: 4,
    gap: 4,
  },
  leadingSlot: {
    width: APP_HEADER_BACK_SLOT_WIDTH,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  titleLane: {
    flex: 1,
    minWidth: 0,
    justifyContent: 'center',
  },
  titleBlock: {
    minWidth: 0,
    justifyContent: 'center',
  },
  titleBlockStacked: {
    gap: 2,
  },
  moduleTitle: {
    color: ticketColors.ink,
    fontSize: 17,
    fontWeight: '700',
    lineHeight: 20,
    flexShrink: 1,
    minWidth: 0,
    fontFamily: ticketFonts.sans,
  },
  /** Square-style location trigger — sin pill; eco de SidebarBranchCard. */
  branchTrigger: {
    alignSelf: 'flex-start',
    minWidth: 0,
    maxWidth: '100%',
    paddingVertical: 0,
    gap: 1,
  },
  branchKicker: {
    fontSize: 10,
    fontWeight: '700',
    letterSpacing: 0.7,
    textTransform: 'uppercase',
    color: ticketColors.ink3,
    fontFamily: ticketFonts.sans,
    lineHeight: 12,
  },
  branchNameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    minWidth: 0,
  },
  branchName: {
    color: ticketColors.ink,
    fontSize: 13,
    fontWeight: '700',
    lineHeight: 16,
    flexShrink: 1,
    minWidth: 0,
    fontFamily: ticketFonts.sans,
  },
  actionsRail: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    flexShrink: 0,
    gap: 8,
    paddingRight: 12,
  },
});
